/**
 * Low houses gone to pieces, on cracked tarmac.
 *
 * The first archetype, and the one the trivial zone is proved against. Parameters only: nothing in
 * here decides where anything goes, it decides what the place is like when something does.
 *
 * The extent is a hundred and sixty metres across, which is a few streets rather than a city and
 * comfortably inside what one nav grid at half-metre cells costs. The densities are low on purpose:
 * a first pass at a place should read as empty and get filled, never the reverse.
 */

import type { ZoneArchetype } from '@/sim/worldgen/zoneArchetypeContract';

export const SUBURBAN_RUINS: ZoneArchetype = {
  id: 'suburbanRuins',
  label: 'Suburban ruins',
  surfaces: {
    ground: 'packedDirt',
    structure: 'weatheredConcrete',
    street: 'crackedAsphalt',
  },
  extent: {
    halfWidth: 80,
    halfDepth: 80,
    // Clears a two storey shell with room to spare, so no floor search ever starts inside a roof.
    ceiling: 12,
  },
  densities: {
    buildings: 0.4,
    props: 2.5,
    enemies: 0.15,
    // Around twenty-five containers over a plan this size, so a crate is worth crossing a street
    // for. Well under the enemy rate on purpose: a container the party walks past is a container
    // that stops being a decision, and a suburb is meant to be searched rather than swept.
    loot: 0.12,
  },
  storeys: {
    // Two, which is what the extent's ceiling was already sized for: a shop with a flat over it is
    // the tallest thing a suburb holds, and the fight up a stair is the reason to build one at all.
    maxStoreys: 2,
    // A third of the ruins big enough to hold a stair get the floor it climbs to. Most of a suburb
    // is bungalows, and a street where every shell is two storeys reads as a terrace of towers.
    upperStoreyChance: 0.35,
    // A ruin's first floor has let go somewhere: about one interior module in seven.
    openingShare: 0.15,
    // A fifth of the shooters wait upstairs: enough that a flat over a shop is worth clearing,
    // not so many that every street is covered from above. Fewer containers than that: a stash
    // upstairs is a detour, and most of the loot stays where the doorways are.
    upstairsEnemyShare: 0.2,
    upstairsLootShare: 0.15,
  },
  relief: {
    amplitudeMetres: 0.55,
    cellMetres: 6,
    dips: 2,
  },
  navigability: {
    // The plan measures a little under nine tenths walkable across every seed tried, and the band
    // is nowhere near it on either side on purpose: it catches a stage that broke, a lot pattern
    // that filled the place in or a buildings pass that placed nothing, never a density nudged by
    // a tenth. A rule that fires on tuning is a rule somebody widens instead of reading.
    minWalkableFraction: 0.55,
    maxWalkableFraction: 0.95,
  },
  layout: {
    // Roughly forty to fifty metres between districts, which puts three of them across a
    // hundred and sixty metre zone: a few streets, the way the extent was chosen for.
    districtSpanMetres: { min: 40, max: 52 },
    // Two lanes and a verge either side. Wide enough that a fight down one has room to move.
    streetWidthMetres: 8,
    // A small house plus its yard at the bottom, a corner block at the top. Above twice the
    // minimum, so no block ever comes out too long to split and too short to build on.
    lotSpanMetres: { min: 10, max: 24 },
    // The perimeter stands six metres out from where the plan stops, so a body walking the edge
    // has ground under it and a wall beside it rather than the end of the slab.
    edgeBandMetres: 6,
    // A quarter of the streets a spanning tree skipped are built anyway: enough that most
    // districts have two ways out, not so many that the place reads as a grid.
    loopEdgeFraction: 0.25,
  },
};
